import chroma from 'chroma-js'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { VIDEO_FILE } from '@disco/constants/FileTypes'
import { PLAYING } from '@disco/constants/MediaStatus'
import {
  pauseTrack,
  playTrack,
  seekTrackPosition,
} from '@disco/state-management/actions/MediaActions'
import type { TrackStatus } from '@disco/state-management/reducers/media'
import {
  getTrackPlaybackStatus,
  getTrackPlaying,
} from '@disco/state-management/selectors/media'
import { formatColor, parseColor } from '@disco/utils/color-utils'
import { getFileTypeByExt } from '@disco/utils/file-utils'

import Canvas from 'components/common/player/waveform/canvas'
import { Flex } from 'components/ds'

const DEFAULT_STATUS = {} as TrackStatus

// check if playback position is within allowable range
const isPlaybackWithinRange = (
  value: number,
  target: number,
  duration: number,
  allowanceRatio: number = 0.01, // 1% of total track duration
) => Math.abs(value - target) <= duration * allowanceRatio

const getSolidColor = (
  index: number,
  fadePos: number,
  activeColorStr: string,
  fadeColorStr: string,
  alphaPos: number,
  theme = 'light',
) => {
  const inactiveColor = parseColor(theme === 'dark' ? '#444444' : '#BBBBBB')
  const fadeColor = parseColor(fadeColorStr)
  const activeColor = parseColor(activeColorStr)
  let resultColor

  if (index < fadePos) {
    resultColor = activeColor
  } else {
    resultColor = inactiveColor
  }

  if (alphaPos !== -1) {
    if (alphaPos > fadePos) {
      // Logic for alphaPos to the right of fadePos
      if (fadePos < index && index < alphaPos) {
        resultColor = fadeColor
      }

      if (alphaPos < index && index < fadePos) {
        resultColor = inactiveColor
      }
    } else {
      // Logic for alphaPos to the left of fadePos
      if (alphaPos < index && index < fadePos) {
        // Fade only the overlap between alphaPos and fadePos
        resultColor = fadeColor
      }
    }
  }

  return formatColor(resultColor)
}

type Props = {
  className?: string
  track: Record<string, any>
  parsedWaveformData?: any
  barWidth?: number
  barGap?: number
  noAnimation?: boolean
  noSeekClick?: boolean
  onSeek?: (_pos: number) => any
  isHoverSeekEnabled?: boolean
  // unified mobile player related props
  scrollable?: boolean
  // scrollable behavior for mobile browsers
  hover?: number
  theme?: 'light' | 'dark'
  height?: number
  waveformLocation?: string
  tracklist?: number[]
  fixedWidth?: number
  externalPlaybackPosition?: number
  onIsCanvasDrawn?: (isCanvasDrawn: boolean) => void
}

const OptimizedWaveform: React.FC<Props> = ({
  className,
  track,
  parsedWaveformData,
  barWidth = 2,
  barGap = 1,
  height = 40,
  noAnimation,
  noSeekClick,
  onSeek,
  isHoverSeekEnabled,
  scrollable,
  hover,
  theme,
  waveformLocation,
  tracklist,
  fixedWidth,
  externalPlaybackPosition,
  onIsCanvasDrawn,
}) => {
  const dispatch = useDispatch()

  // Get playback status for the track
  const status = useSelector(state =>
    track
      ? getTrackPlaybackStatus(state, { trackId: track.id }) || DEFAULT_STATUS
      : DEFAULT_STATUS,
  )

  // Get the currently playing track to check if we need to switch tracks
  const currentlyPlayingTrack = useSelector(getTrackPlaying)

  // Use the pre-parsed waveform data from parent - no need to parse JSON here
  const waveformData = parsedWaveformData
  const playbackPosition = externalPlaybackPosition ?? status.playbackPosition
  const playbackStatus = status.playbackStatus

  const [state, setState] = useState<{
    scrubbing: boolean
    hover: number
    // Holds previous waveform data, which is untyped like parsedWaveformData
    prevWaveform: any
    animationStart: number
    hoverSeekPosition: number
  }>({
    scrubbing: false,
    hover: -1,
    prevWaveform: null,
    animationStart: 0,
    hoverSeekPosition: 0,
  })

  // Refs - simplified approach to avoid heavy effects
  const seekbarRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [color, setColor] = useState('#222222')
  const [colorFade, setColorFade] = useState('#888888')

  const updateColors = useCallback(() => {
    try {
      const containerElement = containerRef.current
      if (!containerElement) return

      const computedStyle = window.getComputedStyle(containerElement)
      const accent9 = computedStyle.getPropertyValue('--accent-9').trim()
      const accent7 = computedStyle.getPropertyValue('--accent-7').trim()

      if (accent9) {
        const newColor = chroma(accent9).hex('rgb')
        setColor(newColor)
      }
      if (accent7) {
        const newColorFade = chroma(accent7).hex('rgb')
        setColorFade(newColorFade)
      }
    } catch (e) {
      console.error('OptimizedWaveform updateColors failed:', e)
    }
  }, [])

  // Calculate responsive width or use fixedWidth as fallback
  const [containerWidth, setContainerWidth] = useState(fixedWidth || 120)
  const dpr = window.devicePixelRatio || 1

  // Set up resize observer to track container width changes
  useEffect(() => {
    if (!fixedWidth && containerRef.current) {
      const updateWidth = () => {
        const rect = containerRef.current?.getBoundingClientRect()
        if (rect) {
          setContainerWidth(rect.width || 120)
        }
      }

      // Update initially
      updateWidth()

      // Set up resize observer
      const resizeObserver = new ResizeObserver(updateWidth)
      resizeObserver.observe(containerRef.current)

      return () => resizeObserver.disconnect()
    }
  }, [fixedWidth])

  const width = fixedWidth || containerWidth

  const getPos = useCallback(
    (evt: any) => {
      const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX
      // Use container ref for position calculation (needed for seek behavior)
      const seekbar = containerRef.current
      if (!seekbar || !track) return 0

      const rect = seekbar.getBoundingClientRect()
      const sx = Math.max(0, Math.min(clientX - rect.left, rect.width))
      const pos = (sx / rect.width) * track.duration

      // Ensure position is within valid range
      return Math.max(0, Math.min(pos, track.duration))
    },
    [track],
  )

  const onSeekHover = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const pos = getPos(e)
      setState(prev => ({
        ...prev,
        hover: pos,
      }))
    },
    [getPos],
  )

  const onSeekClick = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!('touches' in e)) {
        e.preventDefault()
      }

      if (!noSeekClick && track) {
        const pos = getPos(e)
        const isWithinRange = isPlaybackWithinRange(
          playbackPosition,
          pos,
          track.duration,
        )

        const shouldSkipSeek =
          playbackStatus === PLAYING && isHoverSeekEnabled && isWithinRange

        if (!state.scrubbing && shouldSkipSeek) {
          dispatch(pauseTrack(track.id))
          return
        }

        // Check if we're clicking on a different track than the currently playing one
        const isCurrentTrack = currentlyPlayingTrack?.id === track.id

        // Don't dispatch Redux seek for YouTube (999999) to prevent jump-back
        if (track.id !== 999999) {
          if (isCurrentTrack) {
            // For the current track, just seek
            dispatch(seekTrackPosition(track.id, pos, waveformLocation))
          } else {
            // For a new track: dispatch playTrack with the position set via seekTrackPosition first.
            // IMPORTANT: seekTrackPosition must come before playTrack because TRACK_PLAY reducer
            // reads the existing playbackPosition via get(state.trackStatus[trackId], 'playbackPosition', 0).
            // The audio player will then schedule the seek when the new track loads.
            dispatch(seekTrackPosition(track.id, pos, waveformLocation))
            dispatch(
              playTrack(track.id, 'in-app', {
                playbackLocation: waveformLocation || 'Search page',
                tracklist,
              }),
            )
          }
        }

        if (onSeek) onSeek(pos)
      }
    },
    [
      noSeekClick,
      track,
      getPos,
      playbackPosition,
      playbackStatus,
      isHoverSeekEnabled,
      state.scrubbing,
      dispatch,
      waveformLocation,
      tracklist,
      onSeek,
      currentlyPlayingTrack,
    ],
  )

  const handleMouseDown = useCallback(
    (evt: MouseEvent | TouchEvent) => {
      setState(prev => ({
        ...prev,
        scrubbing: true,
      }))

      onSeekClick(evt)

      //The video modal has an index greater than the waveform index, so the onMouseUp event doesn't fire.
      if (track && getFileTypeByExt(track.ext) === VIDEO_FILE) {
        setState(prev => ({
          ...prev,
          scrubbing: false,
        }))
      }
    },
    [onSeekClick, track],
  )

  const handleMouseUp = useCallback(() => {
    setState(prev => ({
      ...prev,
      scrubbing: false,
    }))
  }, [])

  const handleMouseMove = useCallback(
    (evt: MouseEvent | TouchEvent) => {
      const pos = getPos(evt)

      if (state.scrubbing) {
        onSeekClick(evt)
      } else {
        onSeekHover(evt)
      }
      if (isHoverSeekEnabled) {
        setState(prev => ({
          ...prev,
          hoverSeekPosition: pos,
        }))
      }
    },
    [state.scrubbing, onSeekClick, onSeekHover, isHoverSeekEnabled, getPos],
  )

  const handleMouseLeave = useCallback(() => {
    setState(prev => ({
      ...prev,
      hover: -1,
      hoverSeekPosition: 0,
    }))
  }, [])

  const drawCanvas = useCallback(
    ({ ctx, time }: any) => {
      if (!track) return
      const { hoverSeekPosition } = state
      const duration = track.duration || 1
      let { width, height: canvasHeight } = ctx.canvas
      let animationStart: number

      if (state.prevWaveform && !state.animationStart) {
        setState(prev => ({
          ...prev,
          animationStart: time,
        }))
        animationStart = time
      } else if (state.prevWaveform && time - state.animationStart > 800) {
        setState(prev => ({
          ...prev,
          prevWaveform: null,
          animationStart: 0,
        }))
        animationStart = state.animationStart
      } else {
        animationStart = state.animationStart
      }

      ctx.save()
      width = width / dpr
      canvasHeight = canvasHeight / dpr
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, width, canvasHeight)
      let sumAccum = 0
      let sumCount = 0
      let barIndex = 0
      const totalSamples = 1800
      const maxSample = 140
      const barWidthScaled = barWidth * 1.5
      const barSpace = barWidth + barGap
      const totalBars = width / barSpace
      const playbackPos = totalSamples * (playbackPosition / duration)
      const hoverOnlyPlaybackPos =
        isHoverSeekEnabled &&
        hoverSeekPosition &&
        totalSamples * (hoverSeekPosition / duration)
      const hoverPos = totalSamples * ((hover || state.hover) / duration)
      let alphaPos: number, fadePos: number

      if (hover || state.hover !== -1) {
        fadePos = playbackPos
        alphaPos = hoverPos
      } else {
        fadePos = playbackPos
        alphaPos = -1
      }

      if (isHoverSeekEnabled && hoverOnlyPlaybackPos && !playbackPosition) {
        fadePos = hoverOnlyPlaybackPos
        alphaPos = 0
      }

      const coef = (time - animationStart) / 800

      if (waveformData && waveformData.samples) {
        waveformData.samples.forEach((barHeight: number, index: number) => {
          if (state.prevWaveform && !noAnimation) {
            // eslint-disable-next-line no-param-reassign
            barHeight =
              barHeight * coef + state.prevWaveform.samples[index] * (1 - coef) // eslint-disable-line no-param-reassign
          }

          sumAccum += barHeight
          sumCount += 1

          if ((index * totalBars) / totalSamples > barIndex) {
            // eslint-disable-next-line no-param-reassign
            ctx.fillStyle = getSolidColor(
              index,
              fadePos,
              color,
              colorFade,
              alphaPos,
              theme,
            )
            const y = ((sumAccum / sumCount) * canvasHeight) / maxSample
            ctx.fillRect(
              barIndex * barSpace,
              canvasHeight / 2 - Math.max(Math.round(y / 2), 1),
              barWidthScaled,
              Math.max(Math.round(y / 2), 1) * 2,
            )
            barIndex += 1
            sumAccum = 0
            sumCount = 0
          }
        })
      }

      ctx.restore()
      if (onIsCanvasDrawn) {
        onIsCanvasDrawn(true)
      }
    },
    [
      track,
      state,
      dpr,
      barWidth,
      barGap,
      playbackPosition,
      isHoverSeekEnabled,
      hover,
      waveformData,
      noAnimation,
      color,
      colorFade,
      theme,
      onIsCanvasDrawn,
    ],
  )

  useEffect(() => {
    if (containerRef.current) {
      updateColors()
    }
  }, [updateColors])

  const actualHeight = scrollable ? 54 : height

  return (
    // data-row-select-ignore: playing/seeking here must not select the row (STL-1236)
    <Flex ref={containerRef} className={className} data-row-select-ignore>
      <Canvas
        ref={r => {
          seekbarRef.current = r
        }}
        draw={drawCanvas}
        width={width * dpr}
        height={actualHeight * dpr}
        style={{
          width,
          height: actualHeight,
        }}
        realtime={!!state.prevWaveform}
        onMouseDown={scrollable ? undefined : handleMouseDown}
        onMouseUp={scrollable ? undefined : handleMouseUp}
        onMouseMove={scrollable ? undefined : handleMouseMove}
        onMouseLeave={scrollable ? undefined : handleMouseLeave}
        onTouchStart={scrollable ? undefined : handleMouseDown}
        onTouchMove={scrollable ? undefined : handleMouseMove}
        onTouchEnd={scrollable ? undefined : handleMouseLeave}
      />
    </Flex>
  )
}

export default OptimizedWaveform
